import classnames from 'classnames';
import { useContext, useMemo } from 'react';
import { TileKitContext } from './TileKit';
import { InventoryTypeId } from './types';

export const useClassName = (
  tkClassName: string,
  className = '',
  optional: Record<string, boolean> = {}
) => {
  return useMemo(() => {
    return classnames(tkClassName, className, optional);
  }, [className, optional]);
};

export const useNoVehicle = () => {
  return !useContext(TileKitContext).vehicle?.vehicleId;
};

const useInventoryType = () => {
  const { vehicle } = useContext(TileKitContext);

  return useMemo(
    () => ({
      vehicleInventoryType: vehicle?.vehicleInventoryType,
      storeKey: vehicle?.storeKey?.toLowerCase()
    }),
    [vehicle?.vehicleInventoryType, vehicle?.storeKey]
  );
};

export const useIsMarketPlace = (): boolean => {
  const { vehicleInventoryType, storeKey } = useInventoryType();

  return vehicleInventoryType === 4 || storeKey === 'dmp2core';
};

export const useIsManheim = () => {
  const { storeKey } = useInventoryType();

  return useMemo(() => {
    return storeKey === 'manheim';
  }, [storeKey]);
};

export const useIsLightAccident = () => {
  const { storeKey } = useInventoryType();

  return useMemo(() => {
    return storeKey === '148pt';
  }, [storeKey]);
};

export const usePrice = () => {
  const { vehicle } = useContext(TileKitContext);

  return useMemo(() => vehicle?.price, [vehicle?.price]);
};

export const usePriceErrorMessage = () => {
  const { vehicle } = useContext(TileKitContext);

  return useMemo(() => vehicle?.price?.pricingErrorMessage, [vehicle?.price]);
};

const TWO_HOURS = 7200;

export const usePurchaseStatus = () => {
  const { vehicle } = useContext(TileKitContext);

  return useMemo(() => {
    if (!vehicle?.vehicleUnlockDateTime) {
      return { isLocked: false, isOnHold: false };
    }

    const timeLeft = Date.parse(vehicle?.vehicleUnlockDateTime) - +new Date();

    return {
      isLocked: Math.floor(timeLeft / 1000) >= TWO_HOURS,
      isOnHold: Math.floor(timeLeft / 1000) < TWO_HOURS
    };
  }, [vehicle?.vehicleUnlockDateTime]);
};

// TODO confirm with product this can be removed
export const useNearMatch = () => {
  const { vehicle } = useContext(TileKitContext);

  return useMemo(() => {
    if (!vehicle?.matchedTerms || vehicle.matchedTerms.length === 0) {
      return null;
    }

    const [term] = vehicle.matchedTerms;

    return term.replace(/(<[^<]+>)/g, '');
  }, [vehicle?.matchedTerms]);
};

const FILTER_NAMES = {
  bodyStyles: 'Body Style',
  colors: 'Color',
  cylinderCounts: 'Cylinder Count',
  driveTypes: 'Drive Type',
  features: 'Feature',
  fuelTypes: 'Fuel Type',
  modelIds: 'Make/Model',
  mileage: 'Mileage',
  mpg: 'MPG',
  price: 'Price',
  transmissions: 'Transmission',
  trims: 'Trim',
  year: 'Year'
};

export const useMissingFilters = () => {
  const { vehicle } = useContext(TileKitContext);

  return useMemo(() => {
    if (!vehicle?.missingFilters) {
      return null;
    }

    let missingFilters = Object.keys(vehicle.missingFilters).map(
      key => FILTER_NAMES[key]
    );
    missingFilters = missingFilters.filter(Boolean);
    if (missingFilters.length === 0) {
      return null;
    }

    return missingFilters;
  }, [vehicle?.missingFilters]);
};

export const usePriceTerms = () => {
  const { vehicle } = useContext(TileKitContext);

  return useMemo(() => {
    const {
      monthlyPayment = undefined,
      downPayment = undefined,
      downPaymentDetails = undefined,
      apr = undefined,
      term = undefined,
      pricingErrorMessage
    } = vehicle?.price || {};
    const hasTerms =
      monthlyPayment != null &&
      downPayment != null &&
      apr != null &&
      term != null;
    const totalCashDue = downPaymentDetails?.totalCashDue || undefined;

    return {
      monthlyPayment,
      downPayment,
      apr,
      term,
      totalCashDue,
      pricingErrorMessage,
      hasTerms
    };
  }, [vehicle?.price]);
};

export const useHasFinanceFailed = ({ env }) => {
  const isProd = env === 'production';
  const terms = usePriceTerms();

  const hasProdErrored = isProd && terms.pricingErrorMessage;
  const hasErrored = !terms.pricingErrorMessage && !terms.hasTerms;

  return (isProd && hasProdErrored) || hasErrored;
};

export const useIsPreOrder = () => {
  const { vehicle } = useContext(TileKitContext);
  const { isLocked, isOnHold } = usePurchaseStatus();

  if (isLocked || isOnHold) {
    return false;
  }

  return vehicle?.vehicleInventoryType === InventoryTypeId.WIP;
};

export const useIsReservable = () => {
  const { vehicle } = useContext(TileKitContext);
  const { isLocked, isOnHold } = usePurchaseStatus();

  if (isLocked || isOnHold) {
    return false;
  }

  return vehicle?.vehiclePurchaseType === 'Reservable';
};

export const useIsPreOrderable = () => {
  const isPreOrder = useIsPreOrder();
  const isReservable = useIsReservable();

  return isReservable || isPreOrder;
};

export const useDeliveryShippingText = () => {
  const { vehicle } = useContext(TileKitContext);

  return vehicle?.deliveryInfo?.activityType === 'LocalDelivery'
    ? 'Free Shipping • '
    : '';
};
